import React, {FormEvent, ReactNode, useEffect, useState} from 'react';
import {Form, Button, Row, Container, Col, Card, Stack} from 'react-bootstrap';
import {Link, Navigate, useNavigate, useSearchParams} from 'react-router-dom';
import {AuthService, DI, Loading, Box, UnverifiedEmailException, UnverifiedPhoneNumberException} from "shared-lib";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {AuthConst} from "@consts";


const authService: AuthService = DI.get(AuthService);
type Props = {};
type States = { inProgress: boolean, invalid: boolean };

export const SignInPage: React.FunctionComponent<Props> = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {register, handleSubmit, setValue, formState: {errors}} = useForm();
    const {t} = useTranslation();
    const [states, setStates] = useState({} as States);


    useEffect(() => {
        const email = searchParams.get('email');
        if (email) {
            setValue('email', email);
        }
    }, []);

    const onSubmit = async ({email, password, keepLoggedIn}): Promise<void> => {
        setStates({...states, inProgress: true});

        try {
            await authService.signIn(email, password, keepLoggedIn);
            navigate('/', {replace: true});
        } catch (err) {
            if (err instanceof UnverifiedEmailException) {
                navigate(AuthConst.EMAIL_VERIFICATION_PAGE, {replace: true});
            } else if (err instanceof UnverifiedPhoneNumberException) {
                navigate(AuthConst.PHONE_NUMBER_VERIFICATION_PAGE, {replace: true});
            } else {
                setStates({...states, invalid: true, inProgress: false})
            }
        }
    };

    return (
        <Stack className="login-page min-vh-100 justify-content-center align-items-center bg-dark">
            <img className="login-page__logo mb-4" src="./logo_440x64.png"/>
            <Box className="login-page__form">
                <Loading show={states.inProgress}/>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    {
                        states.invalid &&
                        (
                            <p className={`text-danger text-center mb-2`}>
                                {t('signIn.authFailed')}
                            </p>
                        )
                    }
                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Control
                            size="lg"
                            type="email"
                            isInvalid={!!errors.email}
                            placeholder={t('common.input.placeholder.email')}
                            {...register("email", {required: true})}
                        />
                        <Form.Control.Feedback type="invalid" className="text-center">
                            {t('common.input.required')}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formBasicPassword">
                        <Form.Control
                            size="lg"
                            type="password"
                            placeholder={t('common.input.placeholder.password')}
                            isInvalid={!!errors.password}
                            {...register("password", {required: true})}
                        />
                        <Form.Control.Feedback type="invalid" className="text-center">
                            {t('common.input.required')}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3 d-flex justify-content-between">
                        <Form.Check type="checkbox"
                                    {...register("keepLoggedIn",)}
                                    label={t('signIn.keepLoggedIn')}/>
                        <Link to={AuthConst.RESET_PASSWORD_PAGE}>{t('signIn.resetPassword')}</Link>
                    </Form.Group>
                    <Button variant="primary" type="submit" className="w-100 btn-lg">
                        {t('signIn.submit')}
                    </Button>
                </Form>
            </Box>
        </Stack>
    );
}
